'use client';

import useSWR from 'swr';
import { useSWRList } from '@hooks/use-swr-list';
import { useCallback, useMemo } from 'react';
import { useShopifyCartContext } from '../onboarding/OnboardingFlowContainer/ShopifyCartProvider';

const getPlanTypesUrl = (cartId, petId) => {
  if (!cartId || !petId) return null;
  return `/api/v4/onboarding/${cartId}/pets/${petId}/plans/`;
};

const createMealPlanCombinationsUrl = (cartId, petId, mealplanId) => {
  return `/api/v4/onboarding/${cartId}/pets/${petId}/plans/${mealplanId}/`;
};

// Hardcoded to only fetch the plan types currently offered in onboarding.
// To re-enable additional plan types, add their keys back to this array.
// Available keys: 'fresh', 'half_fresh', 'baked', 'mixed'
const PLAN_TYPE_KEYS = ['fresh', 'half_fresh'];

export const useMealPlansCombinations = petId => {
  const { cartId } = useShopifyCartContext();

  // Fetch plan types directly
  const { data: planTypes, isLoading: isPlanTypesLoading } = useSWR(
    cartId && petId ? getPlanTypesUrl(cartId, petId) : null,
  );

  const filteredPlanTypes = useMemo(() => {
    if (!planTypes?.length) return planTypes;
    return planTypes.filter(pt => PLAN_TYPE_KEYS.includes(pt.plan_type_name));
  }, [planTypes]);

  const { data, error, isValidating, mutate } = useSWRList(
    !!filteredPlanTypes?.length && cartId && petId
      ? filteredPlanTypes.map(planType =>
          createMealPlanCombinationsUrl(cartId, petId, planType.mealplan_id),
        )
      : [],
  );

  const combinationsByPlanType = useMemo(() => {
    const result = PLAN_TYPE_KEYS.reduce((acc, key) => {
      acc[key] = {
        planCombinations: [],
        availableRecipes: { fresh: [], baked: [] },
        cadence: null,
        recommendedCombinationId: null,
        recommendedCombination: null,
      };
      return acc;
    }, {});

    if (data && filteredPlanTypes && Array.isArray(data)) {
      data.forEach((combinations, index) => {
        const planType = filteredPlanTypes[index]?.plan_type_name;
        if (!planType) return;
        result[planType] = {
          planCombinations: combinations?.plan_combinations || [],
          availableRecipes: combinations?.available_recipes || {
            fresh: [],
            baked: [],
          },
          cadence: combinations?.cadence,
          recommendedCombinationId: combinations?.recommended_combination_id,
          recommendedCombination: combinations?.recommended_combination_id
            ? combinations?.plan_combinations?.find(
                plan =>
                  plan.combination_id ===
                  combinations.recommended_combination_id,
              )
            : combinations?.plan_combinations?.[0],
        };
      });
    }
    return result;
  }, [data, filteredPlanTypes]);

  const refreshPlans = useCallback(() => mutate(), [mutate]);
  return {
    combinationsByPlanType,
    error,
    isLoading: (cartId && petId && !data && !error) || isPlanTypesLoading,
    isValidating,
    refreshPlans,
  };
};
